/*
 * Cart drawer — Harper & Logan layout in Wear The Clothes styling (2026-10-31).
 * Renders items, tier bar, savings badges and upsells from /cart.js.
 * Tier thresholds come from [data-cart-drawer-tiers] and must match the automatic discounts.
 */
class CartDrawerController {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    const d = this.drawer.dataset;
    this.root = window.Shopify?.routes?.root || '/';
    this.panel = this.drawer.querySelector('.cart-drawer__panel');
    this.body = this.drawer.querySelector('[data-cart-drawer-body]');
    this.footer = this.drawer.querySelector('[data-cart-drawer-footer]');
    this.timerNode = this.drawer.querySelector('[data-cart-drawer-timer]');
    this.timerValue = this.drawer.querySelector('[data-cart-drawer-timer-value]');
    this.tiersNode = this.drawer.querySelector('[data-cart-drawer-tiers-ui]');
    this.liveRegion = this.drawer.querySelector('[data-cart-drawer-live-region]');
    this.drawerCount = this.drawer.querySelector('[data-cart-drawer-count]');
    this.subtotalNode = this.drawer.querySelector('[data-cart-drawer-subtotal]');
    this.discountRow = this.drawer.querySelector('[data-cart-discount-row]');
    this.discountLabel = this.drawer.querySelector('[data-cart-discount-label]');
    this.discountAmount = this.drawer.querySelector('[data-cart-discount-amount]');
    this.checkoutCount = this.drawer.querySelector('[data-cart-drawer-checkout-count]');
    this.checkoutNoun = this.drawer.querySelector('[data-cart-drawer-checkout-noun]');
    this.countNodes = [...document.querySelectorAll('[data-cart-count]')];
    this.toggleNodes = [...document.querySelectorAll('[data-cart-toggle]')];

    this.currency = d.cartCurrency || 'CAD';
    this.emptyTitle = d.cartDrawerEmptyTitle || '';
    this.emptyCopy = d.cartDrawerEmptyCopy || '';
    this.continueUrl = d.cartDrawerContinueUrl || '/collections/all';
    this.continueLabel = d.cartDrawerContinueLabel || 'Continue shopping';
    this.addingLabel = d.cartDrawerAddingLabel || 'Adding...';
    this.loadingLabel = d.cartDrawerLoadingLabel || 'Loading cart';
    this.liveRegionTemplate = d.cartDrawerLiveTemplate || '';
    this.recommendationsUrl = d.cartDrawerRecommendationsUrl || `${this.root}recommendations/products`;
    this.timerMinutes = Number(d.cartDrawerTimerMinutes || 10);
    this.upsellCollection = d.cartDrawerUpsellCollection || '';
    this.upsellMaxPrice = Number(d.cartDrawerUpsellMaxPrice || 0);

    this.tiers = (this.parseJsonNode('[data-cart-drawer-tiers]') || [])
      .map((t) => ({ quantity: Number(t.quantity), percent: Number(t.percent) }))
      .filter((t) => t.quantity > 0 && t.percent > 0)
      .sort((a, b) => a.quantity - b.quantity);

    this.compareAt = new Map();
    Object.entries(this.parseJsonNode('[data-cart-drawer-compare-at-prices]') || {}).forEach(([id, price]) => {
      if (Number(id)) this.compareAt.set(Number(id), Number(price || 0));
    });

    this.cart = null;
    this.upsells = [];
    this.upsellIndex = 0;
    this.upsellSourceId = null;
    this.pendingItemKeys = new Set();
    this.isRefreshing = false;
    this.timerInterval = null;
    this.lastActiveElement = null;
    this.activeAddButton = null;
    this.activeAddLabel = null;
    this.activeAddLabelText = '';

    this.bindUI();
  }

  /* ---------- events ---------- */

  bindUI() {
    this.toggleNodes.forEach((trigger) => {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.addEventListener('click', async (event) => {
        event.preventDefault();
        this.lastActiveElement = trigger;
        this.open();
        await this.refresh();
      });
    });

    this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (event) => {
      if (!this.drawer.classList.contains('is-open')) return;
      if (event.key === 'Escape') this.close();
      else if (event.key === 'Tab') this.trapFocus(event);
    });

    document.addEventListener('submit', (event) => this.handleAddToCart(event));
    this.drawer.addEventListener('click', (event) => this.handleDrawerClick(event));
    this.drawer.addEventListener('change', (event) => this.handleDrawerChange(event));
  }

  async handleAddToCart(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-product-form]')) return;

    const submitter = event.submitter;
    if (!submitter || submitter.getAttribute('name') !== 'add') return;

    event.preventDefault();

    const formData = new FormData(form);
    this.activeAddButton = submitter;
    this.activeAddLabel = submitter.querySelector('[data-add-to-cart-label]');
    this.activeAddLabelText = this.activeAddLabel?.textContent || '';
    this.cacheFormVariantCompareAt(form);
    this.hideAddToCartError(form);

    try {
      this.setAddButtonState(true);
      const response = await fetch(`${this.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.description || data?.message || '');
      }
      await response.json();
      this.lastActiveElement = submitter;
      await this.refresh();
      this.open();
    } catch (error) {
      console.error(error);
      this.showAddToCartError(form, error?.message);
    } finally {
      this.setAddButtonState(false);
    }
  }

  showAddToCartError(form, message) {
    const errorElement = form.querySelector('[data-add-to-cart-error]');
    if (!errorElement) {
      window.location.href = form.action || '/cart';
      return;
    }
    errorElement.textContent = message || errorElement.dataset.genericError || '';
    errorElement.hidden = false;
  }

  hideAddToCartError(form) {
    const errorElement = form.querySelector('[data-add-to-cart-error]');
    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }
  }

  async handleDrawerClick(event) {
    const qtyTrigger = event.target.closest('[data-cart-quantity-change]');
    if (qtyTrigger) {
      const item = qtyTrigger.closest('[data-cart-item-key]');
      const key = item?.dataset.cartItemKey;
      if (!key || this.pendingItemKeys.has(key)) return;
      const input = item.querySelector('[data-cart-quantity-input]');
      const next = Math.max(0, Number(input?.value || 0) + Number(qtyTrigger.dataset.cartQuantityDelta || 0));
      await this.changeItem(key, next);
      return;
    }

    const removeTrigger = event.target.closest('[data-cart-remove]');
    if (removeTrigger) {
      const key = removeTrigger.closest('[data-cart-item-key]')?.dataset.cartItemKey;
      if (key && !this.pendingItemKeys.has(key)) await this.changeItem(key, 0);
      return;
    }

    const nav = event.target.closest('[data-upsell-nav]');
    if (nav) {
      const count = this.upsells.length;
      if (!count) return;
      this.upsellIndex = (this.upsellIndex + Number(nav.dataset.upsellNav) + count) % count;
      this.renderUpsell();
      return;
    }

    const upsellAdd = event.target.closest('[data-upsell-add]');
    if (upsellAdd) await this.addUpsell(upsellAdd);
  }

  async handleDrawerChange(event) {
    const input = event.target.closest('[data-cart-quantity-input]');
    if (input) {
      const key = input.closest('[data-cart-item-key]')?.dataset.cartItemKey;
      if (key && !this.pendingItemKeys.has(key)) await this.changeItem(key, Math.max(0, Number(input.value) || 0));
      return;
    }

    const select = event.target.closest('[data-upsell-variant]');
    if (select) this.updateUpsellPrice(select);
  }

  /* ---------- cart API ---------- */

  async changeItem(key, quantity) {
    this.pendingItemKeys.add(key);
    this.setItemPendingState(key, true);
    try {
      const response = await fetch(`${this.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!response.ok) throw new Error('Unable to update cart item');
      const cart = await response.json();
      this.pendingItemKeys.delete(key);
      await this.render(cart);
    } catch (error) {
      console.error(error);
      this.pendingItemKeys.delete(key);
      await this.refresh();
    }
  }

  async addUpsell(button) {
    const card = button.closest('[data-upsell-card]');
    const variantId = Number(card?.querySelector('[data-upsell-variant]')?.value || button.dataset.variantId || 0);
    if (!variantId) return;

    const label = button.textContent;
    button.disabled = true;
    button.textContent = this.addingLabel;
    try {
      const product = this.upsells[this.upsellIndex];
      const variant = product?.variants?.find((v) => Number(v.id) === variantId);
      if (variant) this.compareAt.set(variantId, Number(variant.compare_at_price || 0));

      const response = await fetch(`${this.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      if (!response.ok) throw new Error('Unable to add upsell');
      await this.refresh(false);
    } catch (error) {
      console.error(error);
      button.disabled = false;
      button.textContent = label;
    }
  }

  async refresh(showSkeleton = true) {
    if (this.isRefreshing) return null;
    this.isRefreshing = true;

    const currentCount = Number(this.drawerCount?.textContent || 0);
    if (showSkeleton && currentCount > 0 && !this.cart) this.showSkeleton(Math.min(currentCount, 3));

    try {
      const response = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      await this.render(cart);
      return cart;
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      this.isRefreshing = false;
    }
  }

  /* compare-at prices are not part of /cart.js, so fetch the product JSON once per missing variant */
  async ensureCompareAt(cart) {
    const missing = (cart.items || []).filter((item) => !this.compareAt.has(Number(item.variant_id)));
    const handles = [...new Set(missing.map((item) => item.handle).filter(Boolean))];
    await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${this.root}products/${encodeURIComponent(handle)}.js`, {
            headers: { Accept: 'application/json' }
          });
          if (!response.ok) return;
          const product = await response.json();
          (product.variants || []).forEach((v) => this.compareAt.set(Number(v.id), Number(v.compare_at_price || 0)));
        } catch (error) {
          console.error(error);
        }
      })
    );
  }

  /* ---------- rendering ---------- */

  async render(cart) {
    await this.ensureCompareAt(cart);
    this.cart = cart;

    const itemCount = cart.item_count || 0;
    if (this.drawerCount) this.drawerCount.textContent = itemCount;
    this.countNodes.forEach((node) => {
      node.textContent = itemCount;
      node.hidden = itemCount === 0;
    });

    if (this.liveRegion) {
      this.liveRegion.textContent = this.liveRegionTemplate
        .replace('[[count]]', String(itemCount))
        .replace('[[subtotal]]', this.formatMoney(cart.total_price || 0));
    }

    this.footer.hidden = itemCount === 0;
    this.timerNode.hidden = itemCount === 0;
    this.tiersNode.hidden = itemCount === 0;

    if (!itemCount) {
      this.stopTimer();
      this.body.innerHTML = `
        <div class="cart-drawer__empty">
          <p class="cart-drawer__empty-title">${this.escapeHtml(this.emptyTitle)}</p>
          <p class="cart-drawer__empty-copy">${this.escapeHtml(this.emptyCopy)}</p>
          <a href="${this.escapeHtml(this.continueUrl)}" class="cart-drawer__button">${this.escapeHtml(this.continueLabel)}</a>
        </div>`;
      return;
    }

    if (this.drawer.classList.contains('is-open')) this.startTimer();
    this.renderTiers(itemCount);
    this.renderSummary(cart);

    // Cart-level (tier) discounts are spread over the lines pro rata, so each line shows what it really costs.
    const cartLevel = (cart.cart_level_discount_applications || []).reduce((sum, a) => sum + Number(a.total_allocated_amount || 0), 0);
    const base = Number(cart.items_subtotal_price || 0);
    const itemsMarkup = cart.items.map((item) => this.itemMarkup(item, cartLevel, base)).join('');
    this.body.innerHTML = `
      <ul class="cart-drawer__items" role="list">${itemsMarkup}</ul>
      <div class="cart-drawer__upsell" data-cart-drawer-upsell hidden></div>`;

    await this.loadUpsells(cart);
  }

  itemMarkup(item, cartLevel = 0, base = 0) {
    const qty = Number(item.quantity || 0);
    const compareUnit = Number(this.compareAt.get(Number(item.variant_id)) || 0);
    const lineBefore = Number(item.final_line_price || 0);
    const finalLine = cartLevel > 0 && base > 0 ? Math.max(0, Math.round(lineBefore - (cartLevel * lineBefore) / base)) : lineBefore;
    let compareLine = compareUnit > Number(item.original_price || item.price || 0) ? compareUnit * qty : 0;
    if (!compareLine && Math.max(Number(item.original_line_price || 0), lineBefore) > finalLine) {
      compareLine = Math.max(Number(item.original_line_price || 0), lineBefore);
    }
    const savePercent = compareLine > finalLine ? Math.round(((compareLine - finalLine) / compareLine) * 100) : 0;

    const options = (item.options_with_values || []).filter((o) => o.value && o.value !== 'Default Title');
    const optionsMarkup = options.length
      ? `<p class="cart-drawer__item-option-names">${this.escapeHtml(options.map((o) => o.name).join(', '))}:</p>
         <p class="cart-drawer__item-option-values">${this.escapeHtml(options.map((o) => o.value).join(', '))}</p>`
      : '';

    const image = item.featured_image?.url || item.image;
    const imageMarkup = image
      ? `<img src="${this.escapeHtml(this.sizedImage(image, 240))}" alt="${this.escapeHtml(item.product_title)}" loading="lazy" width="120" height="120">`
      : '';

    const pending = this.pendingItemKeys.has(item.key);
    const dis = pending ? 'disabled' : '';
    const title = this.escapeHtml(item.product_title);

    return `
      <li class="cart-drawer__item${pending ? ' is-pending' : ''}" data-cart-item-key="${this.escapeHtml(item.key)}" aria-busy="${pending}">
        <a href="${this.escapeHtml(item.url)}" class="cart-drawer__item-media">${imageMarkup}</a>
        <div class="cart-drawer__item-info">
          <a href="${this.escapeHtml(item.url)}" class="cart-drawer__item-title">${title}</a>
          ${optionsMarkup}
          <div class="cart-drawer__qty">
            <button type="button" data-cart-quantity-change data-cart-quantity-delta="-1" aria-label="Decrease quantity of ${title}" ${dis}>&minus;</button>
            <input type="number" min="0" value="${qty}" aria-label="Quantity of ${title}" data-cart-quantity-input ${dis}>
            <button type="button" data-cart-quantity-change data-cart-quantity-delta="1" aria-label="Increase quantity of ${title}" ${dis}>+</button>
          </div>
        </div>
        <div class="cart-drawer__item-prices">
          ${compareLine > finalLine ? `<s class="cart-drawer__item-compare">${this.formatMoney(compareLine)}</s>` : ''}
          <span class="cart-drawer__item-price">${this.formatMoney(finalLine)}</span>
          ${savePercent > 0 ? `<span class="cart-drawer__save">${this.tagIcon()}You save ${savePercent}%</span>` : ''}
          <button type="button" class="cart-drawer__remove" data-cart-remove aria-label="Remove ${title}" ${dis}>${this.trashIcon()}</button>
        </div>
      </li>`;
  }

  renderTiers(itemCount) {
    if (!this.tiers.length) {
      this.tiersNode.hidden = true;
      return;
    }

    const reached = this.tiers.filter((t) => itemCount >= t.quantity).pop();
    const next = this.tiers.find((t) => itemCount < t.quantity);
    let heading;
    if (!next) {
      heading = `<strong>${reached.percent}% discount</strong> unlocked!`;
    } else {
      const missing = next.quantity - itemCount;
      heading = reached
        ? `<strong>${reached.percent}%</strong> unlocked! Add ${missing} more for <strong>${next.percent}% extra discount</strong>`
        : `Add <strong>${missing} more item${missing === 1 ? '' : 's'}</strong> to unlock <strong>${next.percent}% extra discount</strong>`;
    }

    // Tiers sit at equal steps; the fill moves linearly between thresholds.
    const steps = this.tiers.length;
    let fill = 100;
    if (next) {
      const idx = this.tiers.indexOf(next);
      const prevQty = idx === 0 ? 0 : this.tiers[idx - 1].quantity;
      const part = (itemCount - prevQty) / (next.quantity - prevQty);
      fill = ((idx + Math.max(0, Math.min(1, part))) / steps) * 100;
    }

    const nodes = this.tiers
      .map((t, i) => {
        const left = ((i + 1) / steps) * 100;
        const done = itemCount >= t.quantity;
        return `
          <span class="cart-drawer__tier-dot${done ? ' is-reached' : ''}" style="left:${left}%"></span>
          <span class="cart-drawer__tier-label${done ? ' is-reached' : ''}" style="left:${left}%">
            ${this.percentIcon()}
            <span>${t.percent}% discount</span>
          </span>`;
      })
      .join('');

    this.tiersNode.innerHTML = `
      <p class="cart-drawer__tiers-heading">${heading}</p>
      <div class="cart-drawer__tier-track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(fill)}" aria-label="Quantity discount progress">
        <span class="cart-drawer__tier-fill" style="width:${fill}%"></span>
        ${nodes}
      </div>`;
  }

  renderSummary(cart) {
    const totalDiscount = Number(cart.total_discount || 0);
    this.discountRow.hidden = totalDiscount <= 0;
    this.discountAmount.textContent = `−${this.formatMoney(totalDiscount)}`;

    const titles = new Set();
    (cart.cart_level_discount_applications || []).forEach((a) => a.title && titles.add(a.title));
    (cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((a) => a.discount_application?.title && titles.add(a.discount_application.title));
      // /cart/change.js leaves cart-level titles null, but each line still carries the discount title
      (item.discounts || []).forEach((a) => a.title && titles.add(a.title));
    });
    this.discountLabel.textContent = [...titles].join(', ') || 'Discount';

    this.subtotalNode.textContent = this.formatMoney(cart.total_price || 0);
    const count = cart.item_count || 0;
    this.checkoutCount.textContent = count;
    this.checkoutNoun.textContent = count === 1 ? 'item' : 'items';
  }

  /* ---------- upsell ---------- */

  /* Upsell pool: low-ticket accessories (collection order = best-selling), falls back to Shopify's related products */
  async loadCollectionUpsells() {
    if (this.collectionPool) return this.collectionPool;
    this.collectionPool = [];
    if (!this.upsellCollection) return this.collectionPool;
    try {
      const response = await fetch(`${this.root}collections/${encodeURIComponent(this.upsellCollection)}/products.json?limit=50`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return this.collectionPool;
      const data = await response.json();
      const toCents = (value) => (value == null || value === '' ? 0 : Math.round(parseFloat(value) * 100));
      this.collectionPool = (data.products || []).map((p) => {
        const variants = (p.variants || []).map((v) => ({
          id: v.id,
          title: v.title,
          available: v.available !== false,
          price: toCents(v.price),
          compare_at_price: toCents(v.compare_at_price)
        }));
        return {
          id: p.id,
          title: p.title,
          url: `${this.root}products/${p.handle}`,
          featured_image: p.images?.[0]?.src || '',
          options: (p.options || []).map((o) => o.name),
          variants,
          available: variants.some((v) => v.available)
        };
      });
      if (this.upsellMaxPrice > 0) {
        this.collectionPool = this.collectionPool.filter((p) =>
          p.variants.some((v) => v.available && v.price <= this.upsellMaxPrice)
        );
      }
    } catch (error) {
      console.error(error);
    }
    return this.collectionPool;
  }

  async loadUpsells(cart) {
    const inCartIds = new Set(cart.items.map((i) => i.product_id));
    const collectionPool = (await this.loadCollectionUpsells()).filter((p) => p.available && !inCartIds.has(p.id));
    if (collectionPool.length) {
      this.upsells = collectionPool.slice(0, 8);
      if (this.upsellIndex >= this.upsells.length) this.upsellIndex = 0;
      this.renderUpsell();
      return;
    }

    const sourceId = cart.items[0]?.product_id;
    if (!sourceId) return;

    if (sourceId !== this.upsellSourceId) {
      try {
        const response = await fetch(`${this.recommendationsUrl}.json?product_id=${sourceId}&limit=10&intent=related`, {
          headers: { Accept: 'application/json' }
        });
        const data = response.ok ? await response.json() : { products: [] };
        this.upsellPool = data.products || [];
        this.upsellSourceId = sourceId;
        this.upsellIndex = 0;
      } catch (error) {
        console.error(error);
        this.upsellPool = [];
      }
    }

    const inCart = new Set(cart.items.map((i) => i.product_id));
    this.upsells = (this.upsellPool || []).filter((p) => p.available && !inCart.has(p.id)).slice(0, 8);
    if (this.upsellIndex >= this.upsells.length) this.upsellIndex = 0;
    this.renderUpsell();
  }

  renderUpsell() {
    const container = this.body.querySelector('[data-cart-drawer-upsell]');
    if (!container) return;

    const product = this.upsells[this.upsellIndex];
    if (!product) {
      container.hidden = true;
      container.innerHTML = '';
      return;
    }

    const variants = (product.variants || []).filter((v) => v.available);
    const first = variants[0];
    if (!first) {
      container.hidden = true;
      return;
    }

    const hasOptions = !(product.options?.length === 1 && product.variants.length === 1 && first.title === 'Default Title');
    const optionLabel = (product.options || []).map((o) => (typeof o === 'string' ? o : o.name)).join(' / ');
    const select = hasOptions
      ? `<label class="cart-drawer__upsell-option">
           <span>${this.escapeHtml(optionLabel)}:</span>
           <select data-upsell-variant>
             ${variants.map((v) => `<option value="${v.id}">${this.escapeHtml(v.title)}</option>`).join('')}
           </select>
         </label>`
      : `<input type="hidden" data-upsell-variant value="${first.id}">`;

    const image = product.featured_image || product.images?.[0] || '';
    const multiple = this.upsells.length > 1;

    // Every item counts toward the quantity tiers, so tell the shopper what this add-on unlocks.
    const count = Number(this.cart?.item_count || 0);
    const next = this.tiers.find((t) => count < t.quantity);
    let pitch = 'Complete your look';
    if (next) {
      const missing = next.quantity - count;
      pitch = missing <= 1
        ? `Add this to unlock <strong>${next.percent}% extra discount</strong> on everything`
        : `Add this + ${missing - 1} more for <strong>${next.percent}% extra discount</strong>`;
    }

    container.hidden = false;
    container.innerHTML = `
      <p class="cart-drawer__upsell-pitch">${pitch}</p>
      ${multiple ? `<button type="button" class="cart-drawer__upsell-nav" data-upsell-nav="-1" aria-label="Previous product">${this.arrowIcon(true)}</button>` : ''}
      <div class="cart-drawer__upsell-card" data-upsell-card>
        <a href="${this.escapeHtml(product.url)}" class="cart-drawer__upsell-media">
          ${image ? `<img src="${this.escapeHtml(this.sizedImage(image, 200))}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="80" height="80">` : ''}
        </a>
        <div class="cart-drawer__upsell-info">
          <a href="${this.escapeHtml(product.url)}" class="cart-drawer__upsell-title">${this.escapeHtml(product.title)}</a>
          <p class="cart-drawer__upsell-price" data-upsell-price>${this.upsellPriceMarkup(first)}</p>
          ${select}
          <button type="button" class="cart-drawer__upsell-add" data-upsell-add>Add to cart</button>
        </div>
      </div>
      ${multiple ? `<button type="button" class="cart-drawer__upsell-nav" data-upsell-nav="1" aria-label="Next product">${this.arrowIcon(false)}</button>` : ''}`;
  }

  updateUpsellPrice(select) {
    const product = this.upsells[this.upsellIndex];
    const variant = product?.variants?.find((v) => String(v.id) === select.value);
    const priceNode = select.closest('[data-upsell-card]')?.querySelector('[data-upsell-price]');
    if (variant && priceNode) priceNode.innerHTML = this.upsellPriceMarkup(variant);
  }

  upsellPriceMarkup(variant) {
    const price = Number(variant.price || 0);
    const compare = Number(variant.compare_at_price || 0);
    if (compare > price) {
      const pct = Math.round(((compare - price) / compare) * 100);
      return `<strong>${this.formatMoney(price)}</strong> <s>${this.formatMoney(compare)}</s> <span class="cart-drawer__save cart-drawer__save--inline">You save ${pct}%</span>`;
    }
    return `<strong>${this.formatMoney(price)}</strong>`;
  }

  /* ---------- timer ---------- */

  startTimer() {
    if (this.timerInterval) return;
    const key = 'wtcCartReservedUntil';
    const read = () => {
      try {
        return Number(window.sessionStorage.getItem(key) || 0);
      } catch (e) {
        return this.timerFallbackEnd || 0;
      }
    };
    const write = (value) => {
      this.timerFallbackEnd = value;
      try {
        window.sessionStorage.setItem(key, String(value));
      } catch (e) {
        /* storage blocked: keep in memory */
      }
    };

    const tick = () => {
      let end = read();
      const now = Date.now();
      if (!end || end <= now) {
        end = now + this.timerMinutes * 60 * 1000;
        write(end);
      }
      const left = Math.max(0, Math.round((end - now) / 1000));
      const mm = String(Math.floor(left / 60)).padStart(2, '0');
      const ss = String(left % 60).padStart(2, '0');
      if (this.timerValue) this.timerValue.textContent = `${mm}:${ss}`;
    };
    tick();
    this.timerInterval = window.setInterval(tick, 1000);
  }

  stopTimer() {
    window.clearInterval(this.timerInterval);
    this.timerInterval = null;
  }

  /* ---------- open / close ---------- */

  showSkeleton(count = 2) {
    const items = Array.from({ length: count }, () => `
      <div class="cart-drawer__skeleton-item" aria-hidden="true">
        <div class="cart-drawer__skeleton-media"></div>
        <div class="cart-drawer__skeleton-lines"><div></div><div></div><div></div></div>
      </div>`).join('');
    this.body.innerHTML = `<div class="cart-drawer__skeleton" aria-busy="true" aria-label="${this.escapeHtml(this.loadingLabel)}">${items}</div>`;
  }

  setBackgroundInert(isInert) {
    if (isInert) {
      this.inertedElements = [...document.body.children].filter(
        (el) => el !== this.drawer && el.tagName !== 'SCRIPT' && !el.hasAttribute('inert')
      );
      this.inertedElements.forEach((el) => el.setAttribute('inert', ''));
    } else {
      (this.inertedElements || []).forEach((el) => el.removeAttribute('inert'));
      this.inertedElements = [];
    }
  }

  open() {
    if (this.drawer.classList.contains('is-open')) return;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'true'));
    this.setBackgroundInert(true);
    if (Number(this.drawerCount?.textContent || 0) > 0) this.startTimer();
    this.panel.querySelector('#CartDrawerTitle')?.focus();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'false'));
    this.setBackgroundInert(false);
    this.stopTimer();
    this.lastActiveElement?.focus();
  }

  setAddButtonState(isLoading) {
    if (!this.activeAddButton) return;
    this.activeAddButton.disabled = isLoading;
    if (this.activeAddLabel) this.activeAddLabel.textContent = isLoading ? this.addingLabel : this.activeAddLabelText;
  }

  setItemPendingState(key, isPending) {
    const item = this.body?.querySelector(`[data-cart-item-key="${CSS.escape(key)}"]`);
    if (!item) return;
    item.classList.toggle('is-pending', isPending);
    item.setAttribute('aria-busy', String(isPending));
    item.querySelectorAll('button, input').forEach((node) => {
      node.disabled = isPending;
    });
  }

  trapFocus(event) {
    const nodes = [...this.panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter(
      (node) => !node.disabled && node.offsetParent !== null
    );
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- helpers ---------- */

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
      style: 'currency',
      currency: this.currency,
      currencyDisplay: 'narrowSymbol'
    }).format((cents || 0) / 100);
  }

  sizedImage(url, width) {
    if (!url) return '';
    const src = url.startsWith('//') ? `https:${url}` : url;
    try {
      const u = new URL(src, window.location.origin);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) {
      return src;
    }
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  parseJsonNode(selector) {
    const node = this.drawer.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || 'null');
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  cacheFormVariantCompareAt(form) {
    const variantId = Number(form.querySelector('[name="id"]')?.value || 0);
    if (!variantId || this.compareAt.has(variantId)) return;
    const node = form.closest('[data-product-section]')?.querySelector('[data-product-json]');
    if (!node) return;
    try {
      const product = JSON.parse(node.textContent || '{}');
      const variant = (product.variants || []).find((v) => Number(v.id) === variantId);
      if (variant) this.compareAt.set(variantId, Number(variant.compare_at_price || 0));
    } catch (error) {
      console.error(error);
    }
  }

  tagIcon() {
    return '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path fill="currentColor" fill-rule="evenodd" d="M8.575 4.649a3.75 3.75 0 0 1 2.7-1.149h1.975a3.25 3.25 0 0 1 3.25 3.25v2.187a3.25 3.25 0 0 1-.996 2.34l-4.747 4.572a2.5 2.5 0 0 1-3.502-.033l-2.898-2.898a2.75 2.75 0 0 1-.036-3.852l4.254-4.417Zm4.425 3.351a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"/></svg>';
  }

  trashIcon() {
    return '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4h6v3" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  percentIcon() {
    return '<svg class="cart-drawer__tier-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/><path d="M8.5 15.5l7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><circle cx="9" cy="9" r="1.5" fill="currentColor"/><circle cx="15" cy="15" r="1.5" fill="currentColor"/></svg>';
  }

  arrowIcon(left) {
    const d = left ? 'M15 6l-6 6 6 6M9 12h11' : 'M9 6l6 6-6 6M15 12H4';
    return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${d}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawerController();
});
